<script setup>
import { ref, computed } from 'vue'
import SectionTitle from '@weathernews/btos-vuecomponent/with-onebox-common-elements/src/components/SectionTitle.vue'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  targetKey: {
    type: String,
    required: true
  },
  title: {
    type: String,
    default: '',
    required: false
  }
})

let clearId = null
const sliderRef = ref(null)
const imgElementRef = ref(null)
const targetElement = ref(null)
const { data: focusLivecamList } = await useFetch(computed(() => `/api/livecam/focuslist?target=${props.targetKey}`))

const cameraIdIndex = ref(0)
const cameraId = computed(() => focusLivecamList.value[cameraIdIndex.value].id)
const { data: imgList } = await useFetch(computed(() => `/api/livecam/detail?id=${cameraId.value}&type=free`))

const sliderValue = ref(0)
const imgUrl = computed(() => imgList.value.tenmin[sliderValue.value].file)
const selectedTime = computed(() => dayjs.unix(imgList.value.tenmin[sliderValue.value].time).tz('Asia/Tokyo').format('DD(ddd)HH:mm'))

const anime = () => {
  if (sliderValue.value < imgList.value.tenmin.length - 1) {
    sliderValue.value++
  } else {
    sliderValue.value = 0
    if (cameraIdIndex.value === focusLivecamList.value.length - 1) {
      cameraIdIndex.value = 0
    } else {
      cameraIdIndex.value++
    }
  }
  if (sliderValue.value === imgList.value.tenmin.length - 1) {
    clearId = setTimeout(anime, 2000)
    if (imgElementRef.value) {
      setTimeout(() => {
        imgElementRef.value.classList.remove('visible')
      }, 1850)
    }
  } else {
    clearId = setTimeout(anime, 1000)
  }
}
const onClickPlay = () => {
  clearTimeout(clearId)
  clearId = setTimeout(anime, 1000)
}
const onClickStop = () => {
  clearTimeout(clearId)
}
const onClickImage = () => {
  navigateTo(focusLivecamList.value[cameraIdIndex.value].link)
}

const updateSliderBackground = () => {
  if (sliderRef.value) {
    const value = (sliderValue.value / sliderRef.value.max) * 100
    sliderRef.value.style.background = `linear-gradient(to right, #3569c0 0%, #3569c0 ${value}%, #8793ae ${value}%, #8793ae 100%)`
  }
}

const onClickLink = (index) => {
  if (index === cameraIdIndex.value) {
    navigateTo(focusLivecamList.value[index].link)
  } else {
    cameraIdIndex.value = index
  }
}

watch(sliderValue, () => {
  updateSliderBackground()
})

watch(cameraId, () => {
  sliderValue.value = 0
  if (imgElementRef.value) {
    setTimeout(() => {
      imgElementRef.value.classList.add('visible')
    }, 0)
  }
  clearTimeout(clearId)
  clearId = setTimeout(anime, 1250)
})

onMounted(() => {
  if (sliderRef.value) {
    updateSliderBackground()
    sliderRef.value.addEventListener('input', updateSliderBackground)
  }

  const observerOptions = {
    root: null,
    threshold: 1
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        onClickPlay()
      } else {
        onClickStop()
      }
    })
  }, observerOptions)

  if (targetElement.value) {
    observer.observe(targetElement.value)
  }
  onUnmounted(() => {
    if (targetElement.value) {
      observer.unobserve(targetElement.value)
    }
    clearTimeout(clearId)
  })
})

onUnmounted(() => {
  if (sliderRef.value) {
    sliderRef.value.removeEventListener('input', updateSliderBackground)
  }
})
</script>

<template>
  <div class="livecamFeature">
    <SectionTitle :title="`注目のライブカメラ${title}`" />
    <div class="content">
      <div
        ref="targetElement"
        class="box"
      >
        <figure
          class="image"
          @click="onClickImage"
        >
          <img
            ref="imgElementRef"
            :src="imgUrl"
            alt=""
            class="visible"
          >
          <p class="time">
            {{ selectedTime }}
          </p>
        </figure>
        <div class="control">
          <input
            ref="sliderRef"
            v-model="sliderValue"
            type="range"
            min="0"
            :max="imgList.tenmin.length - 1"
            step="1"
            @input="onSliderInput"
          >
        </div>
      </div>
      <div class="block">
        <template
          v-for="(item, index) in focusLivecamList"
          :key="item.id"
        >
          <div class="block__inner">
            <div
              class="link"
              :data-selected="cameraId === item.id"
              @click="onClickLink(index)"
            >
              <p>{{ item.name }}</p>
            </div>
            <button
              v-if="cameraId === item.id"
              :data-selected="cameraId === item.id"
              @click="onClickLink(index)"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.livecamFeature {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    .box {
      display: flex;
      flex-direction: column;
      background-color: #303030;
      .image {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        aspect-ratio: 16 / 9;
        img {
          height: 100%;
          opacity: 0;
          transition: opacity 0.15s linear;
          &.visible {
            opacity: 1;
            transition: opacity 0.25s linear;
          }
        }
        .time {
          position: absolute;
          bottom: 0;
          right: 0;
          padding: 2px 5px;
          background-color: #ffffffab;
          backdrop-filter: blur(4px);
          color: #303030;
          font-size: 12px;
          font-weight: 600;
        }
      }
      .control {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 16px;
        width: 100%;
        input {
          position: relative;
          -webkit-appearance: none;
          appearance: none;
          cursor: pointer;
          outline: none;
          width: 100%;
          height: 3px;
          margin-inline: auto;
          background-color: #8793ae;
          border-radius: 100vmax;
          border: none;
          z-index: 2;
          &::-webkit-slider-thumb {
            -webkit-appearance: none;
            background-color: #3569c0;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            box-shadow: none;
          }
          &::-moz-range-thumb {
            background: #3569c0;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            box-shadow: none;
            border: none;
          }
        }
      }
    }
    .block {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 5px;
      max-height: 183px;
      overflow-y: scroll;
      padding: 6px 10px;
      border: 1px solid #CCC;
      &::-webkit-scrollbar {
        width: 5px;
      }
      &::-webkit-scrollbar-track {
        background: #EEF1F8;
      }
      &::-webkit-scrollbar-thumb {
        background-color: #438FEC;
      }
      .block__inner {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 2px;
        padding-right: 2px;
        padding-bottom: 5px;
        border-bottom: 1px solid #CCC;
        .link {
          padding: 5px 17.5px 5px 10px;
          width: 100%;
          cursor: pointer;
          &[data-selected="true"] {
            width: calc(100% - 17.5px);
            padding-right: 0px;
            background-color: #EEF1F8;
            p {
              color: #303030;
            }
          }
          p {
            color: #3569C0;
            font-size: 14px;
            font-weight: 600;
            line-height: 22px;
          }
        }
        button {
          all: unset;
          cursor: pointer;
          &[data-selected="true"] {
            width: 13.5px;
            height: 31.5px;
            border-radius: 3.6px;
            box-shadow: 0 2px 2px 0 rgb(0 0 0 / 0.15);
            background-color: #fff;
            background-image: url('https://gvs.weathernews.jp/onebox/img/common/icon_arrow_button.svg');
            background-position: center;
            background-repeat: no-repeat;
            @media (any-hover: hover) {
              transition: opacity .2s ease-in-out;
              &:hover {
                box-shadow: 0 2px 6px 0 rgba(#000, .45);
              }
            }
          }
        }
      }
    }
  }
  @media (any-hover: hover) {
    .content {
      .box {
        .image {
          transition: opacity .2s ease-in-out;
          cursor: pointer;
          &:hover {
            opacity: .6;
          }
        }
      }
      .block {
        .link {
          &:not([data-selected="true"]) {
            transition: opacity .2s ease-in-out;
            &:hover {
              opacity: .6;
            }
          }
        }
      }
    }
  }
}
@media screen and (min-width: 830px) {
  .livecamFeature {
    .content {
      flex-direction: row;
      gap: 10px;
      .box {
        max-width: 64%;
        .image {
          height: 274px;
          img {
            max-width: 100%;
            object-fit: cover;
            vertical-align: bottom;
          }
        }
      }
      .block {
        max-height: 290px;
        padding: 10px;
        .block_inner {
          .link {
            p {
              font-size: 15px;
            }
          }
        }
      }
    }
  }
}
@media screen and (max-width: 829px) {
  .livecamFeature {
    .content {
      .box {
        .image {
          max-height: calc(809px / 16 * 9) ;
          height: 100%;
        }
      }
    }
  }
}
</style>
